import { Button, Spin } from "antd";
import { loadVoiceFile } from "api/services/VoiceService";
import { showErrorToast } from "helpers/utils";
import React, { useState } from "react";
import { ReactComponent as PauseIcon } from "../../Icons/vuesax/pause.svg";
import { ReactComponent as PlayIcon } from "../../Icons/vuesax/play.svg";
import { useAudioPlayerContext } from "./AudioPlayerContext";

type TJobSummaryDetail = {
  vfid: number;
  voiceFileName: string;
  duration: number;
  dateOfDictation: string;
  deliveredTime?: string;
  patientName?: string;
  mrn?: string;
  dateOfService?: string;
  reportType?: string;
  status: string;
};

interface VoiceFileNameColumnProps {
  text: string;
  record: TJobSummaryDetail | null;
}

const VoiceFileNameColumn: React.FC<VoiceFileNameColumnProps> = ({ text, record }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { currentAudio, setCurrentAudio, currentlyPlayingId, setCurrentlyPlayingId } =
    useAudioPlayerContext();
  const isPlaying = currentlyPlayingId === record?.vfid;

  if (!record) {
    showErrorToast("Invalid operation");
    return;
  }

  const handlePlayPause = async () => {
    if (isPlaying) {
      currentAudio?.pause();
      setCurrentlyPlayingId(null);
      setCurrentAudio(null);
    } else {
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
        setCurrentlyPlayingId(null);
      }

      setLoading(true);

      try {
        const response = await loadVoiceFile(record?.vfid ?? 0);
        if (!response) {
          throw new Error("Failed to fetch audio file");
        }
        const blob = response;
        const audioUrl = URL.createObjectURL(blob);

        const audio = new Audio(audioUrl);
        const audioContext = new AudioContext();
        const source = audioContext.createMediaElementSource(audio);
        const gainNode = audioContext.createGain();

        gainNode.gain.value = 3.0; // increase volume, try 2.0 to 5.0

        source.connect(gainNode);
        gainNode.connect(audioContext.destination);
        audio.play();

        setCurrentAudio(audio);
        setCurrentlyPlayingId(record?.vfid);

        audio.onended = () => {
          setCurrentlyPlayingId(null);
          setCurrentAudio(null);
        };
      } catch (error) {
        console.error("Error fetching or playing audio:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Button
        type="text"
        onClick={handlePlayPause}
        style={{ marginRight: "8px", color: "#1868db" }}
        icon={
          loading ? (
            <Spin />
          ) : isPlaying ? (
            <PauseIcon width="1.25rem" height="1.25rem" fill="#1868db"/>
          ) : (
            <PlayIcon width="1.25rem" height="1.25rem" fill="#1868db"/>
          )
        }
      />
    </div>
  );
};

export default VoiceFileNameColumn;
